import { useRouter } from 'next/router'
import { useEffectOnce } from 'react-use'

import { BookmarkButtonFragment, BookmarkButton_AddressFragment } from '@web/gql/graphql'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import { isTypename } from '@web/libs/apollo/typeGuard'
import { IS_BROWSER, IS_DEV } from '@web/libs/constants'
import links from '@web/libs/constants/links'
import { lazyLoadResource } from '@web/libs/lazyLoadResource'
import { neloLogger } from '@web/libs/utils'
import { BookmarkInfoOption, Options } from '@web/types/bookmarkSave'

const getGlobalSaveWidgetInstance = () => window.naver?.map?.SaveWidget

export const useCloseSaveWidget = () => {
  const router = useRouter()

  useEffectOnce(() => {
    const handleCloseWidget = () => {
      const widgetInstance = getGlobalSaveWidgetInstance()

      if (widgetInstance) {
        widgetInstance.hide()
      }
    }

    router.events.on('routeChangeStart', handleCloseWidget)
    return () => {
      router.events.off('routeChangeStart', handleCloseWidget)
    }
  })
}

const openMapWidget = (options: Options) => {
  const widgetInstance = getGlobalSaveWidgetInstance()

  if (widgetInstance) {
    widgetInstance.init(options)
    return widgetInstance.show()
  }

  return lazyLoadResource(links.MAP_WIDGET, { type: 'js' })
    .then(() => {
      const newWidgetInstance = getGlobalSaveWidgetInstance()
      if (newWidgetInstance) {
        newWidgetInstance.init(options)
        return newWidgetInstance.show()
      }
      return null
    })
    .catch(error => {
      neloLogger('error', `MAP_WIDGET 로드 실패 : ${error.message}`)
    })
}

export const useMapWidget = () => {
  const { isFromMapsApp } = useGlobalStaticStateContext()

  const DEFAULT_WIDGET_OPTIONS = {
    screenMode: 'popup',
    themeMode: 'system',
    isOutsideClickClose: true,
    svcName: isFromMapsApp ? 'myplace_app' : 'myplace',
    apiEnv: IS_DEV ? 'dev' : 'real',
    url: IS_BROWSER ? window.location.href : '',
  } as Options

  const openBookmark = ({
    placeId,
    place,
    handleBookmarkChange,
  }: {
    placeId?: string
    place?: BookmarkButtonFragment | BookmarkButton_AddressFragment
    handleBookmarkChange: (isBookmarkChecked: boolean) => void
  }) => {
    const getBookmarkInfoOption = ({
      placeId,
      place,
    }: {
      placeId?: string
      place?: BookmarkButtonFragment | BookmarkButton_AddressFragment
    }) => {
      if (placeId || isTypename(place, 'Place')) {
        return {
          type: 'place',
          placeId: placeId || (place as BookmarkButtonFragment)?.id,
        }
      }

      // TODO: MY통합 배포 되면 지도 저장리스트 삭제되니 아래 코드 제거
      if (place) {
        return {
          type: 'address',
          address: place?.name,
          lng: place.coordinate?.longitude,
          lat: place.coordinate?.latitude,
          rcode: place.rcode,
          isNewAddress: place.isNewAddress,
          isDetailAddress: place.isDetailAddress,
        }
      }

      return null
    }

    const bookmarkListOptions: Options = {
      ...DEFAULT_WIDGET_OPTIONS,
      viewMode: 'list',
      bookmarkInfoOption: getBookmarkInfoOption({ place, placeId }) as BookmarkInfoOption,
      onChangeBookmarkState: (isBookmarkRemoved: boolean) => {
        handleBookmarkChange(!isBookmarkRemoved)
      },
    }

    openMapWidget(bookmarkListOptions)
  }

  const openCreateFolder = ({ handleCreateBookmark = async () => {} }) => {
    const createFolderOptions: Options = {
      ...DEFAULT_WIDGET_OPTIONS,
      viewMode: 'folder',
      onClickConfirmFolderView: async () => {
        await handleCreateBookmark()
      },
    }

    openMapWidget(createFolderOptions)
  }

  const openUpdateFolder = ({
    folderId,
    handleUpdateFolder = async () => {},
  }: {
    folderId: number
    handleUpdateFolder: () => Promise<void>
  }) => {
    const updateFolderOptions: Options = {
      ...DEFAULT_WIDGET_OPTIONS,
      viewMode: 'folder',
      folderId,
      onClickConfirmFolderView: async () => {
        await handleUpdateFolder()
      },
    }

    openMapWidget(updateFolderOptions)
  }

  return { openBookmark, openCreateFolder, openUpdateFolder }
}
